import type { CurrencyPrecision, FilterConfig } from '../../../apis/graphql/types';
import type { FilterRowValue, RangeInputNotice } from './FilterRow';

/**
 * The decimals a money filter input accepts: the largest display precision among the selected
 * currencies, or among every currency when none is selected. A currency missing from the list, or a
 * list that has not loaded, falls back to the filter's own `valueDecimals`.
 */
export function resolveMoneyInputDecimals(
  precisions: ReadonlyArray<CurrencyPrecision>,
  selectedCodes: ReadonlyArray<string>,
  fallbackDecimals: number
): number {
  if (precisions.length === 0) return fallbackDecimals;

  const decimalsByCode = new Map(precisions.map(({ code, decimals }) => [code, decimals]));
  const codesInScope = selectedCodes.length > 0 ? selectedCodes : [...decimalsByCode.keys()];

  return Math.max(...codesInScope.map((code) => decimalsByCode.get(code) ?? fallbackDecimals));
}

/**
 * The decimals a range filter input accepts when they depend on the selected currencies: set for
 * money filters, `undefined` for every other filter so it keeps its configured `valueDecimals`.
 */
export function moneyInputDecimals(
  config: FilterConfig,
  precisions: ReadonlyArray<CurrencyPrecision>,
  selectedCodes: ReadonlyArray<string>
): number | undefined {
  if (!config.isMoney) return undefined;
  return resolveMoneyInputDecimals(precisions, selectedCodes, config.valueDecimals ?? 0);
}

function decimalPlaces(value: number): number {
  const [, fraction = ''] = String(value).split('.');
  return fraction.length;
}

function rangeRowValue(value: FilterRowValue): number | null {
  if (value && typeof value === 'object' && 'operator' in value && 'value' in value) return value.value;
  return null;
}

function decimalPlacesLabel(decimals: number): string {
  return `${decimals} decimal ${decimals === 1 ? 'place' : 'places'}`;
}

function displayedPrecision(decimals: number): string {
  return decimals === 0 ? 'without decimals' : `with ${decimalPlacesLabel(decimals)}`;
}

/**
 * The note a range row shows when the selected currencies cap its input below the filter's own
 * `valueDecimals`: a warning when the row already holds a value with more decimals than the cap, which
 * then matches the rows displayed at the rounded value, otherwise a hint explaining the cap.
 */
export function rangeInputNotice(
  config: FilterConfig,
  inputDecimals: number | undefined,
  rowValue: FilterRowValue
): RangeInputNotice | undefined {
  if (inputDecimals === undefined || inputDecimals >= (config.valueDecimals ?? 0)) return undefined;

  const precision = displayedPrecision(inputDecimals);
  const value = rangeRowValue(rowValue);

  if (value !== null && decimalPlaces(value) > inputDecimals) {
    const rounded = value.toFixed(inputDecimals);
    return {
      tone: 'warning',
      message: `The selected currencies display ${precision}, so this value mainly matches rows shown as ${rounded}.`,
    };
  }

  const limit = inputDecimals === 0 ? 'Whole numbers only' : `Up to ${decimalPlacesLabel(inputDecimals)}`;
  return { tone: 'hint', message: `${limit}: the selected currencies display ${precision}.` };
}
